/**
 * Support Mode Banner
 *
 * Shows when admin is impersonating an org.
 * Displays org name, time remaining, and exit/extend controls.
 */

import { useState } from 'react';
import { useSupportMode } from '@/contexts/SupportModeContext';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Building2, Clock, LogOut, RefreshCw, Edit3 } from 'lucide-react';
import { cn } from '@/lib/utils';

export function SupportModeBanner() {
  const {
    actingOrg,
    timeRemainingMs,
    writeActionsEnabled,
    exitSupportMode,
    enableWriteActions,
    disableWriteActions,
    extendSession,
  } = useSupportMode();

  const [showExitConfirm, setShowExitConfirm] = useState(false);

  if (!actingOrg) return null;

  // Format time remaining
  const formatTimeRemaining = (ms: number | null) => {
    if (ms === null) return '--:--';
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const timeRemaining = formatTimeRemaining(timeRemainingMs);
  const isLowTime = timeRemainingMs !== null && timeRemainingMs < 5 * 60 * 1000; // < 5 minutes

  const handleWriteToggle = async (enabled: boolean) => {
    if (enabled) {
      await enableWriteActions();
    } else {
      disableWriteActions();
    }
  };

  return (
    <div className="bg-[hsl(25,90%,45%)] text-white">
      <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-between gap-4">
        {/* Left - Org info */}
        <div className="flex items-center gap-3">
          <Building2 className="h-5 w-5" />
          <div>
            <p className="text-sm font-medium">
              Support Mode: <span className="font-bold">{actingOrg.name || 'Organization'}</span>
            </p>
          </div>
        </div>

        {/* Center - Time remaining */}
        <div className="flex items-center gap-4">
          <div
            className={cn(
              'flex items-center gap-2 px-3 py-1 rounded-full text-sm',
              isLowTime ? 'bg-red-600' : 'bg-white/20'
            )}
          >
            <Clock className="h-4 w-4" />
            <span className="font-mono">{timeRemaining}</span>
          </div>

          <Button
            variant="ghost"
            size="sm"
            onClick={extendSession}
            className="text-white hover:bg-white/20"
          >
            <RefreshCw className="h-4 w-4 mr-1" />
            Extend
          </Button>
        </div>

        {/* Right - Controls */}
        <div className="flex items-center gap-4">
          {/* Write actions toggle */}
          <div className="flex items-center gap-2 bg-white/10 rounded-md px-3 py-1.5">
            <Switch
              id="write-actions"
              checked={writeActionsEnabled}
              onCheckedChange={handleWriteToggle}
              className="data-[state=checked]:bg-green-500"
            />
            <Label
              htmlFor="write-actions"
              className={cn(
                'text-sm cursor-pointer flex items-center gap-1',
                writeActionsEnabled ? 'text-green-200' : 'text-white/70'
              )}
            >
              <Edit3 className="h-3.5 w-3.5" />
              Write Actions
            </Label>
          </div>

          {/* Exit button */}
          <AlertDialog open={showExitConfirm} onOpenChange={setShowExitConfirm}>
            <AlertDialogTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="text-white hover:bg-white/20 border border-white/30"
              >
                <LogOut className="h-4 w-4 mr-1" />
                Exit Support Mode
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent className="bg-[hsl(220,22%,12%)] border-[hsl(220,15%,20%)]">
              <AlertDialogHeader>
                <AlertDialogTitle className="text-white">
                  Exit Support Mode?
                </AlertDialogTitle>
                <AlertDialogDescription className="text-gray-400">
                  You are currently acting as <strong>{actingOrg.name || 'this organization'}</strong>.
                  Exiting will return you to normal admin view.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="bg-transparent border-[hsl(220,15%,25%)] text-gray-400 hover:bg-[hsl(220,15%,15%)] hover:text-white">
                  Cancel
                </AlertDialogCancel>
                <AlertDialogAction
                  onClick={exitSupportMode}
                  className="bg-[hsl(220,70%,50%)] hover:bg-[hsl(220,70%,45%)] text-white"
                >
                  Exit Support Mode
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      {/* Warning when write actions enabled */}
      {writeActionsEnabled && (
        <div className="bg-red-600 text-white text-center text-xs py-1">
          Write actions enabled - Changes will affect the organization
        </div>
      )}
    </div>
  );
}
